import { isUndefined } from 'lodash-es';
import type { KeyboardEvent } from 'react';

import { XYChartSelectionAreaTooltip } from './XYChartSelectionAreaTooltip.js';
import { useGridPlotSize } from '../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { useOverlay } from '../../../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { DynamicSelectionArea } from '../../../core/components/selection-area/DynamicSelectionArea.js';
import { useSynchronizedSelection } from '../../../core/components/shared-chart-interactions/hooks/useSynchronizedSelection.js';
import { RelativeAbsolutePosition } from '../../../core/types/pointer-events.js';
import { getPercentageFromPixel } from '../../../core/utils/get-percentage-from-pixel.js';
import { getSelectionPixels } from '../../../core/utils/get-selection-pixels.js';
import { scaleBandInvert } from '../../../core/utils/scale.js';
import { useAreaData } from '../../hooks/useAreaData.js';
import { useAxesFormatter } from '../../hooks/useAxesFormatter.js';
import { useBarData } from '../../hooks/useBarData.js';
import { useDotData } from '../../hooks/useDotData.js';
import { useFormatCategory } from '../../hooks/useFormatCategory.js';
import { useLineData } from '../../hooks/useLineData.js';
import { useRectData } from '../../hooks/useRectData.js';
import { useSyncedXScaleByPriority } from '../../hooks/useSyncedXScaleByPriority.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { useXYChartData } from '../../hooks/useXYChartData.js';
import { useXYChartId } from '../../hooks/useXYChartId.js';
import { useXYChartScales } from '../../hooks/useXYChartScales.js';
import { useXYChartSelectionAreaRef } from '../../hooks/useXYChartSelectionAreaRef.js';
import { reduceExploreSelect } from '../../store/reducer-explorer.js';
import {
  useInteractionModeState,
  useSelectState,
  useSetState,
} from '../../store/selectors.js';
import { isAnyNumericalScale, isCategoricalScale } from '../../types/guards.js';
import {
  AnyXYChartScale,
  XYChartAxisInternal,
} from '../../types/xy-chart-internals.js';
import { countDatapointsWithSeriesAndCategories } from '../../utils/count-datapoints-with-series-and-categories.js';
import { getSelectedDatapoints } from '../overlay/utils/get-selected-datapoints.js';
import { handleFormatter } from '../overlay/utils/handleFormatter.js';

export const XYChartSelectionArea = () => {
  const setState = useSetState();
  const { overlayRef: chartRef } = useOverlay();
  const axesConfig = useVisibleAxesConfig();
  const axesIdToFormatter = useAxesFormatter();
  const axesScales = useVisibleAxesScales();
  const selectState = useSelectState();
  const { width } = useGridPlotSize();
  const selectionAreaRef = useXYChartSelectionAreaRef();
  const {
    filteredSeriesWithDatapoints,
    axes,
    axesMetadata,
    datapointMetadataMap,
    axisIdToScaleDomain,
  } = useXYChartData();
  const axisIdToScales = useXYChartScales();
  const mode = useInteractionModeState();
  const { updateSelectionState } = useSynchronizedSelection();
  const chartId = useXYChartId();
  const syncedXScale = useSyncedXScaleByPriority();
  const { seriesToMetadata: dotSeriesToMetadata } = useDotData();
  const { seriesToMetadata: lineSeriesToMetadata } = useLineData();
  const { seriesToMetadata: rectSeriesToMetadata } = useRectData();
  const { seriesToMetadata: barSeriesToMetadata } = useBarData();
  const { seriesToMetadata: areaSeriesToMetadata } = useAreaData();
  const formatCategory = useFormatCategory();

  const canZoom = axesMetadata.isZoomEnabled;

  if (isUndefined(selectState) || !chartRef.current) {
    return null;
  }

  const isBottomLabelRotated =
    axesConfig['bottom']?.tickLabelLayout === 'vertical';
  const isTopLabelRotated = axesConfig['top']?.tickLabelLayout === 'vertical';

  const { start, end, selectionPercentage, finished, accessible, variant } =
    selectState;

  const handleWrapperKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' && canZoom) {
      chartRef.current?.focus();
    }
  };

  const handleSelectionChange = ({
    selection,
    accessible,
  }: {
    selection: [RelativeAbsolutePosition, RelativeAbsolutePosition];
    accessible: boolean;
  }) => {
    setState(
      reduceExploreSelect({
        start: selection[0],
        end: selection[1],
        finished: true,
        accessible,
        variant,
        selectionPercentage: getPercentageFromPixel(selection, width),
      }),
    );
    if (mode === 'explore' && syncedXScale) {
      updateSelectionState?.({
        selection,
        chartId,
        scale: syncedXScale,
      });
    }
  };

  const formatterHandler = (
    config?: XYChartAxisInternal,
    scale?: AnyXYChartScale,
  ) => {
    const axisConfig = {
      axisType: config?.type,
      axisScale: scale,
    };
    if (config && isAnyNumericalScale(axisConfig)) {
      return (value: number) =>
        handleFormatter(
          config,
          axesIdToFormatter,
          axisConfig.axisScale,
          value,
          width,
        );
    } else if (isCategoricalScale(axisConfig)) {
      return (value: number) => {
        const raw = scaleBandInvert(axisConfig.axisScale)(value);
        return (raw && formatCategory(config, raw)) ?? raw;
      };
    }
  };

  const selectionStart = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[0]) / 100
    : start.relative.x;
  const selectionEnd = selectState.selectionPercentage
    ? (width * selectState.selectionPercentage[1]) / 100
    : end.relative.x;

  const selectedDatapointsPerSeries = getSelectedDatapoints(
    filteredSeriesWithDatapoints,
    axisIdToScales,
    axes,
    {
      start: selectionStart,
      end: selectionEnd,
    },
    dotSeriesToMetadata,
    lineSeriesToMetadata,
    rectSeriesToMetadata,
    datapointMetadataMap,
    axisIdToScaleDomain,
    barSeriesToMetadata,
    areaSeriesToMetadata,
  );

  const selectionPosition: [
    RelativeAbsolutePosition,
    RelativeAbsolutePosition,
  ] = finished
    ? getSelectionPixels(selectionPercentage, width, [start, end])
    : [start, end];

  const selectedDatapoints = selectedDatapointsPerSeries.map(
    (data) => data.datapoints,
  );

  const datapointCounts = countDatapointsWithSeriesAndCategories(
    selectedDatapoints,
    filteredSeriesWithDatapoints,
    axes,
  );

  return (
    <>
      {variant === 'select' ? (
        <XYChartSelectionAreaTooltip
          {...datapointCounts}
          selectedDatapointsPerSeries={selectedDatapointsPerSeries}
        />
      ) : null}
      <DynamicSelectionArea
        selection={selectionPosition}
        chartRef={chartRef}
        variant={variant}
        focusable={accessible}
        interactable={finished}
        bottomFormatter={formatterHandler(axesConfig.bottom, axesScales.bottom)}
        topFormatter={formatterHandler(axesConfig.top, axesScales.top)}
        onKeyDown={handleWrapperKeyDown}
        onSelectionChange={handleSelectionChange}
        isBottomLabelRotated={isBottomLabelRotated}
        isTopLabelRotated={isTopLabelRotated}
        selectionAreaRef={selectionAreaRef}
        hasMagnifiers={
          !isUndefined(axesConfig.bottom) || !isUndefined(axesConfig.top)
        }
      />
    </>
  );
};
